import type { IFurniture } from '@/editor/data/model/furniture';
import { EDoorWindowType, IWindowData } from '../data/model/model';
import { globalStores, TGlobalStores } from './index';
import { IPoint2d } from '../math/IPoint2d';
import { Curve2dUtils } from '../math/Curve2dUtils';
import { syncDistanceTypeToCenterType } from '../utils/window';
import { BaseListStore } from './base/baseListStore';
import { ILibraryModel } from '@/service/library/types';
import { getRotatedRectangleVertices } from '../math/utils';
import RBush from 'rbush';
import { booleanPolygon, BooleanPolygonType } from '../model/polygon';
import { modelManager } from '../manager/entityManager';
import { EEntityType, IWallEdge } from '../types';
import { getPathFromEdges } from '../utils/edge';
import { dispersePath } from '../utils/shape';

export type TFurnitureInRangeMode = 'intersect' | 'center' | 'contain';

class ModelStore {
  private _gs: TGlobalStores;
  private _furnitureMap: BaseListStore<IFurniture>;
  private _windowMap: BaseListStore<IWindowData>;

  private _models: ILibraryModel[] = [];
  private _defaultDoorWindow: Record<EDoorWindowType, string>;

  private _furnitureRevision: number = 0;
  private _furnitureSpatialIndexCache: { revision: number; index: RBush<{ id: string }> } = {
    revision: 0,
    index: new RBush()
  };

  constructor(gs: TGlobalStores) {
    this._gs = gs;
    // 家具数据的任何变更（含 undo/redo 经 applyChange 直接操作 BaseListStore 的路径）
    // 都必须使空间索引失效，因此 revision 递增统一挂在 onDataChanged 上
    this._furnitureMap = new BaseListStore<IFurniture>(gs, {
      onDataChanged: () => {
        ++this._furnitureRevision;
      }
    });
    this._windowMap = new BaseListStore<IWindowData>(gs);

    this._defaultDoorWindow = {
      [EDoorWindowType.window]: '',
      [EDoorWindowType.frenchWindow]: '',
      [EDoorWindowType.singleDoor]: '',
      [EDoorWindowType.doubleDoor]: '',
      [EDoorWindowType.motherDoor]: ''
    };
  }

  getWindowDoorDefaultBrandGoodId(subType: EDoorWindowType) {
    switch (subType) {
      case EDoorWindowType.window:
        return this._defaultDoorWindow[EDoorWindowType.window];
      case EDoorWindowType.frenchWindow:
        return this._defaultDoorWindow[EDoorWindowType.frenchWindow];
      case EDoorWindowType.singleDoor:
        return this._defaultDoorWindow[EDoorWindowType.singleDoor];
      case EDoorWindowType.doubleDoor:
        return this._defaultDoorWindow[EDoorWindowType.doubleDoor];
      case EDoorWindowType.motherDoor:
        return this._defaultDoorWindow[EDoorWindowType.motherDoor];
    }
  }

  public getModelsByType(key: string) {
    if (!key.startsWith('component.class.two.')) {
      return this._models.filter((model) => model.type === `component.class.two.${key}`);
    }
    return this._models.filter((model) => model.type === key);
  }

  private get modelList() {
    return this._models;
  }

  loadModels(models: ILibraryModel[]) {
    this._models = models;

    for (const key of Object.values(EDoorWindowType)) {
      this._defaultDoorWindow[key] = models
        .filter((model) => model.type === `component.class.two.${key}`)
        .sort((a, b) => a.id.localeCompare(b.id))[0]?.id;
    }
  }

  getModel(keyOrId?: string) {
    return this.modelList.find((m) => m.id === keyOrId) || null;
  }

  // ============================= 门窗 =============================

  getWindowData(windowId: string) {
    return this._windowMap.get(windowId);
  }

  getWindowDatas() {
    return this._windowMap.getList();
  }

  getWindowsByWallId(wallId: string) {
    return this.getWindowDatas().filter((wd) => wd.relativeWallInfo.relativeWallId === wallId);
  }

  addWindowData(windowData: IWindowData) {
    const wallDatas = globalStores.wallStore.getWallDatas();
    const wallData = wallDatas.find((wallData) => wallData.id === windowData.relativeWallInfo.relativeWallId);

    let separateWallData;
    if (!wallData) {
      const separateWallDatas = globalStores.separateWallStore.separateWallDatas;
      separateWallData = separateWallDatas.find(
        (separateWallData) => separateWallData.id === windowData.relativeWallInfo.relativeWallId
      );
    }

    if (!wallData && !separateWallData) return;

    // syncDistanceTypeToCenterType进行一次数据同步
    this._windowMap.add(syncDistanceTypeToCenterType(windowData));
  }

  removeWindowData(windowDataId: string) {
    this._windowMap.remove(windowDataId);
  }

  updateWindowData(windowDataId: string, updateParams: Partial<IWindowData>) {
    this._windowMap.frequentlyUpdate(windowDataId, updateParams);

    // 进行一次数据同步
    const windowData = this.getWindowData(windowDataId);
    if (!windowData) return;
    this._windowMap.frequentlyUpdate(windowDataId, syncDistanceTypeToCenterType(windowData));
  }

  // ============================= 家具 =============================

  getFurnitureDatas() {
    return this._furnitureMap.getList();
  }

  getFurnitureData(furnitureDataId: string) {
    return this._furnitureMap.get(furnitureDataId);
  }

  addFurnitureData(furnitureData: IFurniture) {
    this._furnitureMap.add(furnitureData);
  }

  removeFurnitureData(furnitureDataId: string) {
    this._furnitureMap.remove(furnitureDataId);
  }

  updateFurnitureData(furnitureDataId: string, updateParams: Partial<IFurniture>) {
    this._furnitureMap.frequentlyUpdate(furnitureDataId, updateParams);
  }

  clearAllFurniture() {
    this._furnitureMap.clear();
  }

  clearAllWindow() {
    this._windowMap.clear();
  }

  /**
   * 获取多边形区域内的家具列表
   *
   * @param polygon 多边形区域
   * @param holes 孔洞
   * @param mode 判断模式，`intersect` 表示相交，`center` 表示家具中心点在多边形内，`contain` 表示家具完全被多边形包含
   * @returns 家具列表
   */
  getFurnitureInPathInfos(
    pathInfos: IWallEdge[],
    holeInfos: IWallEdge[][] = [],
    mode: TFurnitureInRangeMode = 'center'
  ) {
    const polygon = dispersePath(getPathFromEdges(pathInfos));
    const holes = holeInfos.map((hole) => dispersePath(getPathFromEdges(hole)));

    return this.getFurnitureInPolygon(polygon, holes, mode);
  }

  getFurnitureInPolygon(polygon: IPoint2d[], holes: IPoint2d[][] = [], mode: TFurnitureInRangeMode = 'center') {
    if (polygon.length < 3) return [];

    const validHoles = holes.filter((hole) => hole.length >= 3);

    // 先用 RBush 索引快速筛选候选家具，再逐个判断是否在多边形内
    const candidateFurnitureDatas = this.getFurnitureSpatialIndex()
      .search({
        minX: Math.min(...polygon.map((p) => p.x)),
        minY: Math.min(...polygon.map((p) => p.y)),
        maxX: Math.max(...polygon.map((p) => p.x)),
        maxY: Math.max(...polygon.map((p) => p.y))
      })
      .map((item) => this.getFurnitureData(item.id))
      .filter((furniture) => furniture !== undefined);
    switch (mode) {
      case 'contain':
        return candidateFurnitureDatas.filter((furniture) => {
          const box = getRotatedRectangleVertices(
            furniture.width,
            furniture.thickness,
            furniture.center,
            furniture.rotation
          );

          // 先排除在孔洞内的家具
          for (const hole of validHoles) {
            if (box.some((point) => Curve2dUtils.isPointInPolygon(point, hole))) {
              return false;
            }
          }
          if (box.every((point) => Curve2dUtils.isPointInPolygon(point, polygon))) {
            return true;
          }
          return false;
        });

      case 'intersect':
        return candidateFurnitureDatas.filter((furniture) => {
          const vertices = getRotatedRectangleVertices(
            furniture.width,
            furniture.thickness,
            furniture.center,
            furniture.rotation
          );

          // 先排除在孔洞内的家具
          for (const hole of validHoles) {
            if (vertices.every((point) => Curve2dUtils.isPointInPolygon(point, hole))) {
              return false;
            }
          }

          // 判断点是否在多边形内
          if (vertices.some((point) => Curve2dUtils.isPointInPolygon(point, polygon))) {
            return true;
          }

          // 用 booleanPolygon 判断多边形和家具矩形是否相交
          if (
            booleanPolygon({ main: [polygon], assistant: [vertices], type: BooleanPolygonType.Intersect }).length > 0
          ) {
            return true;
          }

          return false;
        });
      case 'center':
        return candidateFurnitureDatas.filter((furniture) => {
          const { center } = furniture;

          // 先排除在孔洞内的家具
          for (const hole of validHoles) {
            if (Curve2dUtils.isPointInPolygon(center, hole)) {
              return false;
            }
          }
          if (!Curve2dUtils.isPointInPolygon(center, polygon)) {
            return false;
          }
          return true;
        });
    }
  }

  /** 获取相同模型的家具列表  */
  getSameModelFurnitures(id: string) {
    const furniture = this.getFurnitureData(id);
    if (!furniture) {
      return [];
    }

    return (
      this.getFurnitureDatas()
        .filter(
          ({ modelId, id }) =>
            modelId === furniture.modelId &&
            !this._gs.assemblyStore.isAssemblyFuniture(id) &&
            modelManager.getEntity(id, EEntityType.Furniture)?.visible // 过滤隐藏的 entity
        )
        // 将目标排在第一位
        .sort((a, b) => (a.id === id ? -1 : b.id === id ? 1 : 0))
    );
  }

  /** 获取相同模型的门窗列表 */
  getSameModelDoorWindows(id: string) {
    const data = this.getWindowData(id);
    if (!data) {
      return [];
    }

    return (
      this.getWindowDatas()
        .filter(
          ({ modelId, id }) => modelId === data.modelId && modelManager.getEntity(id, EEntityType.DoorWindow)?.visible
        )
        // 将目标排在第一位
        .sort((a, b) => (a.id === id ? -1 : b.id === id ? 1 : 0))
    );
  }

  clearAll() {
    this.clearAllFurniture();
    this.clearAllWindow();
  }

  coverDatas(windowDatas: IWindowData[], furnitureDatas: IFurniture[]) {
    this.clearAll();

    windowDatas.forEach((windowData) => {
      this.addWindowData(syncDistanceTypeToCenterType(windowData));
    });
    furnitureDatas.forEach((furniture) => {
      this.addFurnitureData(furniture);
    });
  }

  /**
   * 获取家具的 RBush 索引缓存，如果家具有更新，则重新构建 RBush
   */
  private getFurnitureSpatialIndex() {
    if (this._furnitureSpatialIndexCache.revision !== this._furnitureRevision) {
      this._furnitureSpatialIndexCache.index.clear();
      const data = this.getFurnitureDatas().map((furniture) => {
        const vertices = getRotatedRectangleVertices(
          furniture.width,
          furniture.thickness,
          furniture.center,
          furniture.rotation
        );
        return {
          id: furniture.id,
          minX: Math.min(...vertices.map((v) => v.x)),
          minY: Math.min(...vertices.map((v) => v.y)),
          maxX: Math.max(...vertices.map((v) => v.x)),
          maxY: Math.max(...vertices.map((v) => v.y))
        };
      });
      this._furnitureSpatialIndexCache.index.load(data);
      this._furnitureSpatialIndexCache.revision = this._furnitureRevision;
    }
    return this._furnitureSpatialIndexCache.index;
  }
}

export default ModelStore;
